<template>
  <div
    class="drawer-overlay"
    :class="{ 'is-open': isOpen }"
    @click="handleOverlayClick"
  >
    <div
      class="drawer-container"
      :class="{ 'is-open': isOpen }"
      @click.stop
    >
      <!-- Drawer Header -->
      <div class="drawer-header">
        <div class="drawer-title">
          <div class="title-left">
            <Icon
              :name="stateConfig.icon"
              class="drawer-icon"
              size="16"
            />
            <h2 :class="`drawer-title-${activeState}`">
              {{ drawerTitle }}
            </h2>
          </div>
          <div class="title-right">
            <!-- Translate Dropdown -->
            <div
              v-if="stateConfig.showTranslateDropdown"
              ref="translateDropdownRef"
              class="translate-dropdown"
            >
              <button
                class="translate-button"
                :class="{ 'is-active': isTranslateDropdownOpen }"
                aria-label="Translation options"
                @click="toggleTranslateDropdown"
              >
                <Icon
                  name="lucide:globe"
                  class="translate-icon"
                />
                <span class="translate-text">Translate</span>
                <Icon
                  name="lucide:chevron-down"
                  class="dropdown-arrow"
                  :class="{ 'is-rotated': isTranslateDropdownOpen }"
                />
              </button>

              <div
                class="translate-dropdown-menu"
                :class="{ 'is-open': isTranslateDropdownOpen }"
              >
                <div
                  v-for="language in languages"
                  :key="language.code"
                  class="translate-dropdown-item"
                  @click="selectLanguage(language.code)"
                >
                  <span>{{ language.name }}</span>
                </div>
              </div>
            </div>

            <button
              class="drawer-action-button"
              aria-label="Close drawer"
              @click="handleClose"
            >
              <Icon
                name="lucide:minus"
                size="16"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Drawer Content -->
      <div class="drawer-content">
        <component
          :is="currentComponent"
          :client-info="clientInfo"
          @submit="handleFormSubmit"
          @cancel="handleClose"
          @send-message="handleChatMessage"
          @suggestion-click="handleSuggestionClick"
          @open-inbox="handleOpenInbox"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from "#imports";
import InboxContent from "./ContactUs/DrawerContent.vue";
import AiChatContent from "./Chat/DrawerContent.vue";
const props = defineProps({
  isOpen: { type: Boolean, required: false, default: false },
  activeState: { type: String, required: false, default: "closed" },
  clientInfo: { type: Object, required: false, default: () => ({}) }
});
const emit = defineEmits(["close", "submit", "stateChange"]);
const languages = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" }
];
const isTranslateDropdownOpen = ref(false);
const translateDropdownRef = ref();
const stateConfig = computed(() => {
  const configs = {
    "inbox": {
      title: "Contact Us",
      icon: "lucide:mail",
      component: InboxContent,
      showTranslateDropdown: false
    },
    "ai-chat": {
      title: "Ask AI",
      icon: "lucide:sparkles",
      component: AiChatContent,
      showTranslateDropdown: false
    },
    "closed": {
      title: "",
      icon: "",
      maxHeight: "0px",
      component: null,
      showTranslateDropdown: false
    }
  };
  return configs[props.activeState] || configs.closed;
});
const currentComponent = computed(() => stateConfig.value.component);
const clientInfo = computed(() => props.clientInfo);
const drawerTitle = computed(() => stateConfig.value.title);
function handleOverlayClick() {
  emit("close");
}
function handleClose() {
  emit("close");
}
function handleFormSubmit(formData) {
  emit("submit", formData);
}
async function handleChatMessage(_message) {
}
function handleSuggestionClick(_suggestion) {
}
function handleOpenInbox() {
  emit("stateChange", "inbox");
}
function toggleTranslateDropdown() {
  isTranslateDropdownOpen.value = !isTranslateDropdownOpen.value;
}
function selectLanguage(_languageCode) {
  isTranslateDropdownOpen.value = false;
}
function handleClickOutside(event) {
  if (translateDropdownRef.value && !translateDropdownRef.value.contains(event.target)) {
    isTranslateDropdownOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.drawer-overlay{bottom:0;left:0;opacity:0;pointer-events:none;position:fixed;right:0;top:0;transition:all .3s cubic-bezier(.4,0,.2,1);visibility:hidden;z-index:999}.drawer-overlay.is-open{opacity:1;pointer-events:auto;visibility:visible}.drawer-container{--chat-bubble-offset:80px;background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:4px;bottom:100px;box-shadow:0 20px 64px rgba(0,0,0,.15);display:flex;flex-direction:column;max-height:calc(85svh - var(--chat-bubble-offset));max-width:568px;opacity:0;overflow-y:auto;position:fixed;right:72px;transform:translateY(20px) scale(.95);transition:all .3s cubic-bezier(.4,0,.2,1)}.drawer-container.is-open{opacity:1;transform:translateY(0) scale(1)}.drawer-header{align-items:center;border-bottom:1px solid #e5e7eb;display:flex;flex-direction:column;justify-content:space-between;padding:8px 16px}.drawer-header-actions{margin-top:20px}.drawer-header-actions,.drawer-title{align-items:center;display:flex;gap:12px}.drawer-title{flex-direction:row;justify-content:space-between;width:100%}.drawer-icon{--color-blue-500:#1850c1;color:var(--color-blue-500)}.drawer-title .title-left{--space-300:12px;align-items:center;display:flex;gap:var(--space-300)}.drawer-title .title-right{--space-600:24px;align-items:center;display:flex;gap:var(--space-600)}.drawer-title .title-left h2{font-size:14px;font-weight:600;line-height:18px;margin:0}.drawer-action-button{align-items:center;background:transparent;border:none;border-radius:6px;color:#6b7280;cursor:pointer;display:flex;height:32px;justify-content:center;transition:all .2s;width:32px}.drawer-action-button:hover{background:#e5e7eb;color:#374151}.translate-dropdown{position:relative}.translate-button{align-items:center;background:transparent;border:none;border-radius:6px;color:#374151;cursor:pointer;display:flex;font-size:14px;gap:8px;padding:4px 12px;position:relative;transition:all .2s}.translate-button:hover{background:#f9fafb}.translate-button.is-active{background:#f3f4f6}.translate-icon{color:#6b7280;font-size:16px}.translate-text{font-weight:500;white-space:nowrap}.dropdown-arrow{color:#9ca3af;font-size:14px;transition:transform .2s}.dropdown-arrow.is-rotated{transform:rotate(180deg)}.translate-dropdown-menu{background:#fff;border:1px solid #d1d5db;border-radius:6px;box-shadow:0 10px 25px rgba(0,0,0,.1);margin-top:4px;min-width:180px;opacity:0;position:absolute;right:0;top:100%;transform:translateY(-8px);transition:all .2s cubic-bezier(.4,0,.2,1);visibility:hidden;z-index:1000}.translate-dropdown-menu.is-open{opacity:1;transform:translateY(0);visibility:visible}.translate-dropdown-item{align-items:center;color:#374151;cursor:pointer;display:flex;font-size:14px;padding:10px 14px;transition:background-color .2s}.translate-dropdown-item:hover{background:#f9fafb}.translate-dropdown-item:first-child{border-radius:6px 6px 0 0}.translate-dropdown-item:last-child{border-radius:0 0 6px 6px}.drawer-content{box-sizing:border-box;display:flex;flex:1;flex-direction:column;overflow:hidden;width:100%}@media (max-width:768px){.drawer-container{bottom:90px;left:24px;max-height:70vh;right:24px;width:auto}.drawer-header{padding:14px 16px}}@media (max-width:480px){.drawer-container{bottom:80px;left:16px;max-height:60vh;right:16px}}
</style>
